<template>
  <v-container fluid>
    <div class="breadcrumbs-container">
      <Breadcrumbs />
    </div>
    <Header :text="$tc('sbomVisualizer.title', 2)" />
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="pa-6 mb-6 sbom-card" style="background-color: #e2f1fc;">
          <v-card-title class="headline font-weight-bold mb-1" style="color: #203f9a;">
            Sunshine - SBOM visualization tool
          </v-card-title>

          <v-card-subtitle class="mb-2 subtitle-2" style="color: black;">
            CycloneDX JSON file
          </v-card-subtitle>

          <v-card-text>
            <v-file-input
              :label="loadedFileName || 'Select a CycloneDX JSON file'"
              accept=".json"
              outlined
              dense
              prepend-icon="mdi-paperclip"
              class="sbom-file-input"
              @change="handleFileUpload"
              color="#203f9a"
              background-color="white"
              style="border-radius: 6px; color: black;"
              :value="loadedFileName ? [{ name: loadedFileName }] : undefined"
            />

            <v-switch
              v-model="enrichData"
              label="Enrich data with EPSS and CISA KEV"
              class="mt-2 sbom-switch"
              inset
              color="#203f9a"
              style="--v-theme-switch-label-color: #203f9a;"
            />
          </v-card-text>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <!-- Summary Table (custom, matches screenshot) -->
        <v-card class="pa-5 mb-6 sbom-card" style="background-color: #e2f1fc;">
          <v-card-title class="font-weight-bold" style="color: #203f9a;">Summary</v-card-title>
          <v-card-text>
            <v-simple-table
              v-if="summaryReady"
              class="sbom-summary-table"
              style="background: white; border-radius: 8px;"
            >
              <thead>
                <tr>
                  <th style="width: 180px;">No. of Components</th>
                  <th class="sbom-vuln-col">Vulnerabilities</th>
                  <th style="width: 100px;">Spec Version</th>
                  <th style="width: 420px;">Serial Number</th>
                  <th style="width: 60px;">Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {{ summaryStats.components }}
                  </td>
                  <td>
                    <div class="sbom-vuln-badges">
                      <span
                        v-for="{ sev, count } in filteredVulnCounts"
                        :key="sev"
                        class="sbom-vuln-badge-wrap"
                      >
                        <v-icon
                          small
                          :style="
                            `vertical-align: middle; margin-right: 6px; ${
                              sev === 'none' ? 'color:#4caf50' : ''
                            }`
                          "
                          v-if="sev === 'critical'"
                          >mdi-alert-decagram</v-icon
                        >
                        <v-icon
                          small
                          :style="
                            `vertical-align: middle; margin-right: 6px; ${
                              sev === 'none' ? 'color:#4caf50' : ''
                            }`
                          "
                          v-else-if="sev === 'high'"
                          >mdi-alert</v-icon
                        >
                        <v-icon
                          small
                          :style="
                            `vertical-align: middle; margin-right: 6px; ${
                              sev === 'none' ? 'color:#4caf50' : ''
                            }`
                          "
                          v-else-if="sev === 'medium'"
                          >mdi-alert-circle</v-icon
                        >
                        <v-icon
                          small
                          :style="
                            `vertical-align: middle; margin-right: 6px; ${
                              sev === 'none' ? 'color:#4caf50' : ''
                            }`
                          "
                          v-else-if="sev === 'low'"
                          >mdi-alert-outline</v-icon
                        >
                        <v-icon
                          small
                          :style="`vertical-align: middle; margin-right: 6px; color:#4caf50`"
                          v-else-if="sev === 'none'"
                          >mdi-check-circle</v-icon
                        >
                        <span
                          class="sbom-vuln-badge-number"
                          :style="`background:${getVulnBadgeColor(sev)};color:white;`"
                          >{{ sev === 'none' && count === 0 ? '0' : count }}</span
                        >
                      </span>
                    </div>
                  </td>
                  <td>
                    {{ summaryStats.specVersion }}
                  </td>
                  <td style="font-size: 0.95em;">
                    {{ summaryStats.serialNumber }}
                  </td>
                  <td>
                    {{ summaryStats.version }}
                  </td>
                </tr>
                <tr>
                  <td colspan="5" style="background: #f7fbff;">
                    <span style="font-style: italic;">
                      <b>Max EPSS</b> &rarr; {{ summaryStats.maxEpss }}
                      <span style="margin-left: 24px;">
                        <b>Vulnerabilities in CISA KEV</b> &rarr; {{ summaryStats.cisaKevCount }}
                      </span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-alert v-else type="info" outlined dense>
              Summary table will appear here...
            </v-alert>
          </v-card-text>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <v-card class="pa-5 mb-6 sbom-card" color="primary" dark>
          <v-card-title class="font-weight-bold text-secondary-700">Components chart</v-card-title>
          <v-card-text>
            <div id="chart-container-inner" class="sbom-chart"></div>
          </v-card-text>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <!-- Components Table (match screenshot) -->
        <v-card class="pa-5 mb-6 sbom-card" style="background-color: #e2f1fc;">
          <v-card-title class="font-weight-bold" style="color: #203f9a;">
            Components table
          </v-card-title>
          <v-card-text>
            <v-data-table
              v-if="summaryReady"
              :headers="componentsTableHeaders"
              :items="componentsTableRowsPaged"
              class="elevation-2 sbom-table certifai-table"
              dense
              :items-per-page="summaryPagination.itemsPerPage"
              :footer-props="footerProps"
              hide-default-footer
              hide-default-header
            >
              <!-- Custom header with sorting controls -->
              <template v-slot:header>
                <thead>
                  <tr>
                    <th
                      @click="handleComponentSort('component')"
                      class="text-start sortable-header"
                    >
                      Component
                      <v-icon small v-if="componentSortOptions.sortBy === 'component'">
                        {{ componentSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th
                      @click="handleComponentSort('dependsOn')"
                      class="text-start sortable-header"
                    >
                      Depends on
                      <v-icon small v-if="componentSortOptions.sortBy === 'dependsOn'">
                        {{ componentSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th
                      @click="handleComponentSort('dependencyOf')"
                      class="text-start sortable-header"
                    >
                      Dependency of
                      <v-icon small v-if="componentSortOptions.sortBy === 'dependencyOf'">
                        {{ componentSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th
                      @click="handleComponentSort('directVulns')"
                      class="text-start sortable-header"
                    >
                      Direct vulnerabilities
                      <v-icon small v-if="componentSortOptions.sortBy === 'directVulns'">
                        {{ componentSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th
                      @click="handleComponentSort('transitiveVulns')"
                      class="text-start sortable-header"
                    >
                      Transitive vulnerabilities
                      <v-icon small v-if="componentSortOptions.sortBy === 'transitiveVulns'">
                        {{ componentSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th @click="handleComponentSort('license')" class="text-start sortable-header">
                      License
                      <v-icon small v-if="componentSortOptions.sortBy === 'license'">
                        {{ componentSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                  </tr>
                </thead>
              </template>

              <!-- Depends on column -->
              <template v-slot:item.dependsOn="{ item }">
                <div v-if="item.dependsOn && item.dependsOn.length">
                  <span v-for="dep in item.dependsOn" :key="dep" class="sbom-chip">{{ dep }}</span>
                </div>
                <span v-else>-</span>
              </template>
              <!-- Dependency of column -->
              <template v-slot:item.dependencyOf="{ item }">
                <div v-if="item.dependencyOf && item.dependencyOf.length">
                  <span v-for="dep in item.dependencyOf" :key="dep" class="sbom-chip">{{
                    dep
                  }}</span>
                </div>
                <span v-else>-</span>
              </template>
              <!-- Direct vulnerabilities column -->
              <template v-slot:item.directVulns="{ item }">
                <div v-if="item.directVulns && item.directVulns.length">
                  <span v-for="v in item.directVulns" :key="v" class="sbom-chip sbom-vuln-chip">{{
                    v
                  }}</span>
                </div>
                <span v-else>-</span>
              </template>
              <!-- Transitive vulnerabilities column -->
              <template v-slot:item.transitiveVulns="{ item }">
                <div v-if="item.transitiveVulns && item.transitiveVulns.length">
                  <span
                    v-for="v in item.transitiveVulns"
                    :key="v"
                    class="sbom-chip sbom-vuln-chip"
                    >{{ v }}</span
                  >
                </div>
                <span v-else>-</span>
              </template>
              <!-- License column -->
              <template v-slot:item.license="{ item }">
                <div v-if="item.license && item.license.length">
                  <span
                    v-for="lic in item.license"
                    :key="lic"
                    class="sbom-chip sbom-license-chip"
                    >{{ lic }}</span
                  >
                </div>
                <span v-else>-</span>
              </template>
              <!-- Footer -->
              <template v-slot:footer>
                <div class="sbom-table-footer" style="color: black;">
                  <span>
                    Showing {{ summaryPagination.from }}-{{ summaryPagination.to }} of
                    {{ summaryPagination.total }}
                  </span>
                  <v-spacer />
                  <v-select
                    v-model="summaryPagination.itemsPerPage"
                    :items="[10, 20]"
                    dense
                    hide-details
                    style="max-width: 70px; color: black;"
                    :menu-props="{ maxHeight: '200px' }"
                    item-color="black"
                    persistent-hint
                  >
                    <template v-slot:selection>
                      <span>{{ summaryPagination.itemsPerPage || 10 }}</span>
                    </template>
                  </v-select>
                  <v-btn icon @click="prevSummaryPage" :disabled="summaryPagination.page === 1">
                    <v-icon style="color: black;">mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    @click="nextSummaryPage"
                    :disabled="
                      summaryPagination.page * summaryPagination.itemsPerPage >=
                        summaryPagination.total
                    "
                  >
                    <v-icon style="color: black;">mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
            <v-alert v-else type="info" outlined dense>
              Components table will appear here...
            </v-alert>
          </v-card-text>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <v-card class="pa-5 mb-6 sbom-card" style="background-color: #e2f1fc;">
          <v-card-title class="font-weight-bold" style="color: #203f9a;">
            Vulnerabilities table
          </v-card-title>
          <v-card-text>
            <v-data-table
              v-if="summaryReady"
              :headers="[]"
              :items="vulnRowsPaged"
              class="elevation-2 sbom-table certifai-table"
              dense
              hide-default-header
              :items-per-page="vulnPagination.itemsPerPage"
              :footer-props="footerProps"
              hide-default-footer
              style="background-color: white; color: black;"
            >
              <!-- Custom table header with sorting controls -->
              <template v-slot:header>
                <thead>
                  <tr>
                    <th @click="handleVulnSort('id')" class="text-start sortable-header">
                      Vulnerability
                      <v-icon small v-if="vulnSortOptions.sortBy === 'id'">
                        {{ vulnSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th @click="handleVulnSort('severity')" class="text-start sortable-header">
                      Severity
                      <v-icon small v-if="vulnSortOptions.sortBy === 'severity'">
                        {{ vulnSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th @click="handleVulnSort('score')" class="text-start sortable-header">
                      Score
                      <v-icon small v-if="vulnSortOptions.sortBy === 'score'">
                        {{ vulnSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                    <th @click="handleVulnSort('component')" class="text-start sortable-header">
                      Component
                      <v-icon small v-if="vulnSortOptions.sortBy === 'component'">
                        {{ vulnSortOptions.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                      </v-icon>
                    </th>
                  </tr>
                </thead>
              </template>

              <!-- Custom body content template -->
              <template v-slot:body="{ items }">
                <tbody>
                  <tr v-for="item in items" :key="item.id + '-' + item.component">
                    <td>{{ item.id }}</td>
                    <td>
                      <span
                        :style="{
                          'background-color': getSeverityColor(item.severity),
                          color:
                            item.severity === 'low' || item.severity === 'medium'
                              ? 'black'
                              : 'white',
                          'border-radius': '6px',
                          padding: '2px 8px',
                          'font-weight': 'bold',
                          'font-size': '0.95em',
                        }"
                      >
                        {{
                          item.severity
                            ? item.severity.charAt(0).toUpperCase() + item.severity.slice(1)
                            : '-'
                        }}
                      </span>
                    </td>
                    <td>
                      {{ item.score !== undefined && item.score !== null ? item.score : '-' }}
                    </td>
                    <td>{{ item.component || '-' }}</td>
                  </tr>
                </tbody>
              </template>

              <!-- Footer -->
              <template v-slot:footer>
                <div
                  class="sbom-table-footer"
                  style="color: black; display: flex; align-items: center;"
                >
                  <span>
                    Showing {{ vulnPagination.from }}-{{ vulnPagination.to }} of
                    {{ vulnPagination.total }}
                  </span>
                  <v-spacer />
                  <v-select
                    v-model="vulnPagination.itemsPerPage"
                    :items="[5, 10, 20]"
                    dense
                    hide-details
                    style="max-width: 70px; color: black;"
                    item-color="black"
                  />
                  <v-btn icon @click="prevVulnPage" :disabled="vulnPagination.page === 1">
                    <v-icon style="color: black;">mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    @click="nextVulnPage"
                    :disabled="
                      vulnPagination.page * vulnPagination.itemsPerPage >= vulnPagination.total
                    "
                  >
                    <v-icon style="color: black;">mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
            <v-alert v-else type="info" outlined dense>
              Vulnerabilities table will appear here...
            </v-alert>
          </v-card-text>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <v-card class="pa-4 mb-4 sbom-card" color="primary" dark>
          <v-card-title class="font-weight-bold text-secondary-700">Log</v-card-title>
          <v-card-text>
            <v-alert type="info" outlined dense v-if="log">{{ log }}</v-alert>
          </v-card-text>
        </v-card>

        <!-- <v-card class="pa-2 mt-4 sbom-card" color="primary" dark>
          <v-card-text class="text-center">
            Sunshine - SBOM visualization tool | Made by
            <a href="https://www.linkedin.com/in/lucacapacci/" target="_blank">Luca Capacci</a> |
            Contributor
            <a href="https://www.linkedin.com/in/mattiafierro/" target="_blank">Mattia Fierro</a> |
            <a href="https://github.com/CycloneDX/Sunshine/" target="_blank">GitHub repository</a> |
            <a href="https://github.com/CycloneDX/Sunshine/blob/main/LICENSE" target="_blank"
              >License</a
            >
          </v-card-text>
        </v-card> -->
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import 'datatables.net';
import * as echarts from 'echarts';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import Header from '@/components/helpers/Header';

export default {
  name: 'SbomVisualizer',
  components: {
    Breadcrumbs,
    Header,
  },
  data() {
    return {
      enrichData: true,
      summaryReady: false,
      log: '',
      loadedFileName: null,
      summaryRows: [],
      vulnRows: [],
      vulnRowsPaged: [],
      allVulnRows: [], // Store all vulnerabilities for global sorting
      vulnSortOptions: { sortBy: 'id', sortDesc: false }, // Track current sort options
      summaryPagination: { page: 1, itemsPerPage: 10, total: 0, from: 1, to: 5 },
      vulnPagination: { page: 1, itemsPerPage: 5, total: 0, from: 1, to: 5 },
      footerProps: {
        itemsPerPageOptions: [5, 10, 20],
        showFirstLastPage: false,
        showCurrentPage: false,
        showSelect: false,
      },
      summaryStats: {
        components: 0,
        vulnCounts: { critical: 0, high: 0, medium: 0, low: 0, none: 0 },
        maxEpss: 0,
        cisaKevCount: 0,
        specVersion: '',
        serialNumber: '',
        version: '',
        tools: [],
      },
      componentsTableHeaders: [
        { text: 'Component', value: 'component' },
        { text: 'Depends on', value: 'dependsOn' },
        { text: 'Dependency of', value: 'dependencyOf' },
        { text: 'Direct vulnerabilities', value: 'directVulns' },
        { text: 'Transitive vulnerabilities', value: 'transitiveVulns' },
        { text: 'License', value: 'license' },
      ],
      componentsTableRows: [],
      componentsTableRowsPaged: [],
      componentsTableSearch: {
        component: '',
        dependsOn: '',
        dependencyOf: '',
        directVulns: '',
        transitiveVulns: '',
        license: '',
      },
      allComponentRows: [], // Store all components for global sorting
      componentSortOptions: { sortBy: 'component', sortDesc: false }, // Track sort options
    };
  },
  mounted() {
    // Check if there's uploaded file data from the SBOM upload page
    this.loadUploadedFileData();
  },
  computed: {
    filteredVulnCounts() {
      return Object.entries(this.summaryStats.vulnCounts)
        .filter(([sev, count]) => count > 0 || sev === 'none')
        .map(([sev, count]) => ({ sev, count }));
    },
  },
  watch: {
    summaryRows(val) {
      this.summaryPagination.total = val.length;
      this.updateSummaryPagination();
      this.updateComponentsTableRows();
    },
    vulnRows(val) {
      this.vulnPagination.total = val.length;
      this.updateVulnPagination();
      this.updateVulnRowsPaged();
    },
    'summaryPagination.page': 'updateSummaryAndComponents',
    'summaryPagination.itemsPerPage': 'updateSummaryAndComponents',
    'vulnPagination.page': 'updateVulnRowsPaged',
    'vulnPagination.itemsPerPage': 'updateVulnRowsPaged',
    componentsTableSearch: {
      handler: 'updateComponentsTableRows',
      deep: true,
    },
  },
  methods: {
    async loadUploadedFileData() {
      const fileData = localStorage.getItem('sbomFileData');
      if (!fileData) return;

      try {
        const parsedFileData = JSON.parse(fileData);
        this.loadedFileName = parsedFileData.name;
        this.log = `Loading file: ${parsedFileData.name}...`;

        let jsonContent = null;

        // Check if the data is compressed
        const isCompressed = localStorage.getItem('sbomJsonCompressed') === 'true';
        if (isCompressed) {
          // Load and decompress the data
          const compressed = localStorage.getItem('sbomJsonContent');
          const LZString = await import('lz-string');
          const jsonStr = LZString.default.decompressFromUTF16(compressed);
          jsonContent = JSON.parse(jsonStr);
          this.log = `Decompressed file: ${parsedFileData.name}`;
        }
        // Check if data is chunked
        else if (localStorage.getItem('sbomJsonChunked') === 'true') {
          const chunkCount = parseInt(localStorage.getItem('sbomJsonChunkCount') || '0');
          let jsonStr = '';

          // Reassemble chunks
          for (let i = 0; i < chunkCount; i++) {
            const chunk = localStorage.getItem(`sbomJsonChunk_${i}`);
            if (chunk) {
              jsonStr += chunk;
              // Clean up chunk immediately
              localStorage.removeItem(`sbomJsonChunk_${i}`);
            }
          }

          // Clean up chunk metadata
          localStorage.removeItem('sbomJsonChunked');
          localStorage.removeItem('sbomJsonChunkCount');

          jsonContent = JSON.parse(jsonStr);
          this.log = `Assembled chunked file: ${parsedFileData.name}`;
        }
        // Regular storage method
        else {
          const jsonContentStr = localStorage.getItem('sbomJsonContent');
          if (jsonContentStr) {
            jsonContent = JSON.parse(jsonContentStr);
            this.log = `Loaded file: ${parsedFileData.name}`;
          } else {
            throw new Error('JSON content not found');
          }
        }

        // Process the data
        if (jsonContent) {
          await this.visualizeSbom(jsonContent);
          this.log = `Auto-loaded file: ${parsedFileData.name}`;
        }

        // Clean up
        localStorage.removeItem('sbomFileData');
        localStorage.removeItem('sbomJsonContent');
        localStorage.removeItem('sbomJsonCompressed');
      } catch (error) {
        console.error('Error loading uploaded file data:', error);
        this.log = 'Error loading uploaded file data: ' + error.message;
      }
    },

    handleFileUpload(file) {
      if (!file || !(file instanceof Blob)) {
        // Clear selections if invalid file
        this.loadedFileName = null;
        return;
      }
      this.loadedFileName = file.name;
      const reader = new FileReader();
      reader.onload = async e => {
        try {
          const json = JSON.parse(e.target.result);
          this.log = '';
          await this.visualizeSbom(json);
        } catch (err) {
          this.log = 'Error parsing JSON: ' + err.message;
        }
      };
      reader.readAsText(file);
    },

    async visualizeSbom(sbomJson) {
      // --- Extract components ---
      const components = (sbomJson.components || []).map(c => ({
        name: c.name,
        version: c.version || '-',
        type: c.type || '-',
        license:
          (c.licenses &&
            c.licenses[0] &&
            (c.licenses[0].license?.id || c.licenses[0].license?.name)) ||
          '-',
        vulnerabilities: (c.vulnerabilities || []).map(v => ({
          id: v.id,
          severity: (v.ratings && v.ratings[0]?.severity) || 'info',
          score: (v.ratings && v.ratings[0]?.score) || 0,
          epss: v.epss,
          cisaKev: v.cisaKev,
        })),
        bomRef: c['bom-ref'] || null,
      }));

      // --- Build bom-ref map for fast lookup ---
      const bomRefMap = {};
      components.forEach(c => {
        if (c.bomRef) bomRefMap[c.bomRef] = c;
      });

      // --- Add root-level vulnerabilities to components, create placeholder if missing ---
      (sbomJson.vulnerabilities || []).forEach(vuln => {
        (vuln.affects || []).forEach(aff => {
          let comp = aff.ref && bomRefMap[aff.ref];
          if (!comp && aff.ref) {
            comp = {
              name: aff.ref,
              version: '-',
              type: '-',
              license: '-',
              vulnerabilities: [],
              bomRef: aff.ref,
              _placeholder: true,
            };
            components.push(comp);
            bomRefMap[aff.ref] = comp;
          }
          if (comp) {
            let rating =
              Array.isArray(vuln.ratings) && vuln.ratings.length
                ? vuln.ratings.find(
                    r => r.method && ['CVSSv31', 'CVSSv3', 'CVSSv4'].includes(r.method),
                  ) || vuln.ratings[0]
                : null;
            comp.vulnerabilities.push({
              id: vuln.id,
              severity: rating && rating.severity ? rating.severity.toLowerCase() : 'info',
              score: (rating && rating.score) || 0,
              epss: vuln.epss,
              cisaKev: vuln.cisaKev,
            });
          }
        });
      });

      components.forEach(c => {
        delete c.bomRef;
        delete c._placeholder;
      });

      this.summaryRows = components;
      this.summaryReady = true;

      // --- Calculate summary stats ---
      const vulnCounts = { critical: 0, high: 0, medium: 0, low: 0, none: 0 };
      let maxEpss = 0,
        cisaKevCount = 0;
      components.forEach(c =>
        (c.vulnerabilities || []).forEach(v => {
          const sev = (v.severity || '').toLowerCase();
          if (sev === 'critical') vulnCounts.critical++;
          else if (sev === 'high') vulnCounts.high++;
          else if (sev === 'medium') vulnCounts.medium++;
          else if (sev === 'low') vulnCounts.low++;
          else vulnCounts.none++;
          if (typeof v.epss === 'number' && v.epss > maxEpss) maxEpss = v.epss;
          if (v.cisaKev === true || v.cisaKev === 1) cisaKevCount++;
        }),
      );

      this.summaryStats = {
        components: components.length,
        vulnCounts,
        maxEpss: maxEpss ? maxEpss.toFixed(5) : '0',
        cisaKevCount,
        specVersion: sbomJson.specVersion || sbomJson['specVersion'] || '-',
        serialNumber: sbomJson.serialNumber || sbomJson['serialNumber'] || '-',
        version: sbomJson.version || sbomJson['version'] || '-',
        tools: this.extractTools(sbomJson),
      };

      // --- Render ECharts sunburst ---
      this.$nextTick(() => {
        if (echarts) {
          const chartContainer = document.getElementById('chart-container-inner');
          // Dispose of any existing chart instance
          const existingChart = echarts.getInstanceByDom(chartContainer);
          if (existingChart) {
            existingChart.dispose();
          }

          const chart = echarts.init(chartContainer);
          chart.setOption({
            backgroundColor: '#032c57',
            series: [
              {
                type: 'sunburst',
                data: components.map(c => ({
                  name: c.name + (c.version ? ' ' + c.version : ''),
                  value: 1,
                  itemStyle: { color: '#1C538E' },
                })),
                radius: [0, '90%'],
                label: { color: '#baccde', fontWeight: 'bold', fontFamily: 'inherit' },
              },
            ],
          });
        }
      });

      // --- Vulnerabilities table ---
      // Create a map to deduplicate vulnerabilities with the same ID
      const vulnMap = new Map();
      components.forEach(c => {
        (c.vulnerabilities || []).forEach(v => {
          // Create a unique key for each vulnerability
          const vulnKey = `${v.id}-${c.name}-${c.version}`;

          vulnMap.set(vulnKey, {
            id: v.id,
            severity: v.severity,
            score: v.score,
            component: c.name,
          });
        });
      });

      // Convert map to array for the table
      this.allVulnRows = Array.from(vulnMap.values());
      // Apply initial sort by ID
      this.sortVulnerabilities();

      this.log = `Loaded ${components.length} components.`;

      // --- Build dependency graph for components table ---
      const refToKey = {};
      components.forEach(c => {
        const key = c.name + (c.version ? ' ' + c.version : '');
        if (c.bomRef) refToKey[c.bomRef] = key;
        if (c.name && c.version) {
          refToKey[`${c.name}==${c.version}`] = key;
          refToKey[`${c.name}@${c.version}`] = key;
          refToKey[`${c.name} ${c.version}`] = key;
        }
        refToKey[key] = key;
      });

      const dependsOnMap = {},
        dependencyOfMap = {};
      (sbomJson.dependencies || []).forEach(dep => {
        const ref = dep.ref || dep['ref'];
        let fromKey = refToKey[ref] || refToKey[ref && ref.trim()] || ref;
        if (!fromKey && typeof ref === 'string') {
          const match = ref.match(/^(.+?)(==|@)(.+)$/);
          if (match)
            fromKey =
              refToKey[`${match[1]} ${match[3]}`] ||
              refToKey[`${match[1]}==${match[3]}`] ||
              refToKey[`${match[1]}@${match[3]}`] ||
              `${match[1]} ${match[3]}`;
        }
        if (!fromKey) return;
        (dep.dependsOn || []).forEach(dref => {
          let toKey = refToKey[dref] || refToKey[dref && dref.trim()] || dref;
          if (!toKey && typeof dref === 'string') {
            const match = dref.match(/^(.+?)(==|@)(.+)$/);
            if (match)
              toKey =
                refToKey[`${match[1]} ${match[3]}`] ||
                refToKey[`${match[1]}==${match[3]}`] ||
                refToKey[`${match[1]}@${match[3]}`] ||
                `${match[1]} ${match[3]}`;
          }
          if (!toKey) return;
          if (!dependsOnMap[fromKey]) dependsOnMap[fromKey] = [];
          if (!dependsOnMap[fromKey].includes(toKey)) dependsOnMap[fromKey].push(toKey);
          if (!dependencyOfMap[toKey]) dependencyOfMap[toKey] = [];
          if (!dependencyOfMap[toKey].includes(fromKey)) dependencyOfMap[toKey].push(fromKey);
        });
      });

      this.componentsTableRows = components.map(c => {
        const key = c.name + (c.version ? ' ' + c.version : '');
        return {
          component: key,
          dependsOn: dependsOnMap[key] || [],
          dependencyOf: dependencyOfMap[key] || [],
          directVulns: (c.vulnerabilities || []).map(v => v.id),
          transitiveVulns: [],
          license: c.license ? [c.license] : [],
        };
      });

      // Store all components for global sorting
      this.allComponentRows = [...this.componentsTableRows];

      // Apply initial sort by component name
      this.sortComponents();
    },
    extractTools(sbomJson) {
      let tools = [];
      if (Array.isArray(sbomJson.tools)) {
        tools = sbomJson.tools.map(t => ({
          type: t.type || '-',
          group: t.group || '-',
          name: t.name || '-',
          version: t.version || '-',
        }));
      } else if (sbomJson.metadata && Array.isArray(sbomJson.metadata.tools)) {
        tools = sbomJson.metadata.tools.map(t => ({
          type: t.type || '-',
          group: t.group || '-',
          name: t.name || '-',
          version: t.version || '-',
        }));
      }
      return tools;
    },
    getSeverityColor(severity) {
      const map = {
        critical: '#a10a0a',
        high: '#ff4633',
        medium: '#ff9335',
        low: '#fccd58',
        info: '#7dd491',
        information: '#7dd491',
      };
      return map[(severity || '').toLowerCase()] || '#bcbcbc';
    },
    getVulnBadgeColor(sev) {
      const map = {
        critical: '#a10a0a',
        high: '#ff4633',
        medium: '#ff9335',
        low: '#fccd58',
        none: '#7dd491',
      };
      return map[sev] || '#bcbcbc';
    },
    updateSummaryPagination() {
      const { page, itemsPerPage, total } = this.summaryPagination;
      this.summaryPagination.from = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
      this.summaryPagination.to = Math.min(page * itemsPerPage, total);
    },
    updateVulnPagination() {
      const { page, itemsPerPage, total } = this.vulnPagination;
      this.vulnPagination.from = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
      this.vulnPagination.to = Math.min(page * itemsPerPage, total);
    },
    prevSummaryPage() {
      if (this.summaryPagination.page > 1) this.summaryPagination.page--;
    },
    nextSummaryPage() {
      if (
        this.summaryPagination.page * this.summaryPagination.itemsPerPage <
        this.summaryPagination.total
      )
        this.summaryPagination.page++;
    },
    prevVulnPage() {
      if (this.vulnPagination.page > 1) this.vulnPagination.page--;
    },
    nextVulnPage() {
      if (this.vulnPagination.page * this.vulnPagination.itemsPerPage < this.vulnPagination.total)
        this.vulnPagination.page++;
    },
    updateSummaryAndComponents() {
      this.updateSummaryPagination();
      this.updateComponentsTableRows();
    },
    updateComponentsTableRows() {
      // Start with the globally sorted data
      let rows = [...this.componentsTableRows];
      const s = this.componentsTableSearch;
      if (s.component)
        rows = rows.filter(r => r.component.toLowerCase().includes(s.component.toLowerCase()));
      if (s.dependsOn)
        rows = rows.filter(r =>
          (r.dependsOn || []).some(d => d.toLowerCase().includes(s.dependsOn.toLowerCase())),
        );
      if (s.dependencyOf)
        rows = rows.filter(r =>
          (r.dependencyOf || []).some(d => d.toLowerCase().includes(s.dependencyOf.toLowerCase())),
        );
      if (s.directVulns)
        rows = rows.filter(r =>
          (r.directVulns || []).some(d => d.toLowerCase().includes(s.directVulns.toLowerCase())),
        );
      if (s.transitiveVulns)
        rows = rows.filter(r =>
          (r.transitiveVulns || []).some(d =>
            d.toLowerCase().includes(s.transitiveVulns.toLowerCase()),
          ),
        );
      if (s.license)
        rows = rows.filter(r =>
          (r.license || []).some(l => l.toLowerCase().includes(s.license.toLowerCase())),
        );
      const { page, itemsPerPage } = this.summaryPagination;
      this.summaryPagination.total = rows.length;
      this.summaryPagination.from = rows.length === 0 ? 0 : (page - 1) * itemsPerPage + 1;
      this.summaryPagination.to = Math.min(page * itemsPerPage, rows.length);
      this.componentsTableRowsPaged = rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    },

    // New method for global component sorting
    sortComponents() {
      const { sortBy, sortDesc } = this.componentSortOptions;

      // Create a copy of the full dataset to avoid modifying the original
      const sortedRows = [...this.allComponentRows];

      // Apply sorting based on current sort options
      sortedRows.sort((a, b) => {
        let result = 0;

        // Handle array fields (dependsOn, dependencyOf, etc.)
        if (Array.isArray(a[sortBy]) || Array.isArray(b[sortBy])) {
          const aArr = Array.isArray(a[sortBy]) ? a[sortBy] : [];
          const bArr = Array.isArray(b[sortBy]) ? b[sortBy] : [];

          // First compare by array length
          if (aArr.length !== bArr.length) {
            result = aArr.length - bArr.length;
          }
          // If lengths are equal and not empty, compare by first element
          else if (aArr.length > 0 && bArr.length > 0) {
            const aVal = String(aArr[0]).toLowerCase();
            const bVal = String(bArr[0]).toLowerCase();

            if (aVal < bVal) result = -1;
            else if (aVal > bVal) result = 1;
          }
        }
        // Handle scalar fields
        else {
          const aVal = String(a[sortBy] || '').toLowerCase();
          const bVal = String(b[sortBy] || '').toLowerCase();

          if (aVal < bVal) result = -1;
          else if (aVal > bVal) result = 1;
        }

        // Reverse if descending order is requested
        return sortDesc ? -result : result;
      });

      // Update the main components array with sorted data
      this.componentsTableRows = sortedRows;

      // Reset to first page when sort changes
      this.summaryPagination.page = 1;

      // Update the paginated display
      this.updateComponentsTableRows();
    },

    // Handle component header click for sorting
    handleComponentSort(column) {
      if (this.componentSortOptions.sortBy === column) {
        // Toggle direction if same column
        this.componentSortOptions.sortDesc = !this.componentSortOptions.sortDesc;
      } else {
        // Switch to new column and reset direction
        this.componentSortOptions.sortBy = column;
        this.componentSortOptions.sortDesc = false;
      }

      // Apply the sort
      this.sortComponents();
    },

    // New method to handle sorting globally
    sortVulnerabilities() {
      const { sortBy, sortDesc } = this.vulnSortOptions;

      // Create a copy to avoid modifying the original array
      const sortedRows = [...this.allVulnRows];

      // Apply sorting based on current sort options
      sortedRows.sort((a, b) => {
        let result = 0;

        if (sortBy === 'severity') {
          // Custom sort for severity levels
          const severityRanking = {
            critical: 5,
            high: 4,
            medium: 3,
            low: 2,
            information: 1,
            info: 1,
            unknown: 0,
            none: 0,
          };

          const aValue = severityRanking[(a.severity || '').toLowerCase()] || 0;
          const bValue = severityRanking[(b.severity || '').toLowerCase()] || 0;

          result = aValue - bValue;
        } else if (sortBy === 'score') {
          // Numeric sort for scores
          const aValue = parseFloat(a.score || 0);
          const bValue = parseFloat(b.score || 0);

          result = aValue - bValue;
        } else {
          // String comparison for other fields
          const aValue = (a[sortBy] || '').toString().toLowerCase();
          const bValue = (b[sortBy] || '').toString().toLowerCase();

          if (aValue < bValue) result = -1;
          else if (aValue > bValue) result = 1;
        }

        // Reverse if descending order
        return sortDesc ? -result : result;
      });

      // Update the main vulnerability rows array with sorted data
      this.vulnRows = sortedRows;

      // Reset to first page whenever sort changes
      this.vulnPagination.page = 1;

      // Update the paginated display
      this.updateVulnRowsPaged();
    },

    // Handle header click for sorting
    handleVulnSort(column) {
      if (this.vulnSortOptions.sortBy === column) {
        // Toggle direction if same column
        this.vulnSortOptions.sortDesc = !this.vulnSortOptions.sortDesc;
      } else {
        // Switch to new column and reset direction
        this.vulnSortOptions.sortBy = column;
        this.vulnSortOptions.sortDesc = false;
      }

      // Apply the sort
      this.sortVulnerabilities();
    },

    updateVulnRowsPaged() {
      const rows = this.vulnRows; // These are already sorted
      const { page, itemsPerPage } = this.vulnPagination;
      this.vulnPagination.total = rows.length;
      this.vulnPagination.from = rows.length === 0 ? 0 : (page - 1) * itemsPerPage + 1;
      this.vulnPagination.to = Math.min(page * itemsPerPage, rows.length);
      this.vulnRowsPaged = rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    },
  },
};
</script>

<style scoped>
.sbom-card {
  background: #e2f1fc !important;
  border-radius: 14px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}
.sbom-file-input {
  background: #e2f1fc !important;
  border-radius: 6px;
  color: #baccde !important;
}
.sbom-switch .v-input--selection-controls__ripple {
  background: #1c538e !important;
}
.sbom-table {
  background: white !important;
  border-radius: 8px;
  color: black !important;
}
.sbom-table .v-data-table-header th {
  color: #baccde !important;
  font-weight: 600;
  background: #032c57 !important;
}
.sbom-table .v-data-table__wrapper {
  background: #0a3767 !important;
}
.sbom-table-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 0 0 0;
  color: black;
  font-size: 0.95rem;
}
.sbom-chart {
  height: 400px;
  background: #032c57;
  border-radius: 8px;
  margin-bottom: 8px;
}
a {
  color: #baccde;
  text-decoration: underline;
}
#output {
  white-space: pre-line;
  background: #fff;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 10px;
  font-family: 'Courier New', 'Lucida Console', monospace;
}
#footer {
  background-color: #032c57;
  color: #baccde;
  text-align: center;
}
.sbom-summary-table th,
.sbom-summary-table td {
  color: black !important;
  font-size: 1.05em;
  vertical-align: middle;
  padding: 10px 8px;
}
.sbom-summary-table th {
  background: #032c57 !important;
  color: #baccde !important;
  font-weight: 600;
}
.sbom-summary-table tr:nth-child(2) td {
  border-bottom: none !important;
}
.sbom-summary-table tr:last-child td {
  background: #f7fbff !important;
  color: #203f9a !important;
  font-size: 1em;
  border-top: 1px solid #e2f1fc;
}
/* Add more space to vulnerabilities column and badges */
.sbom-vuln-col {
  min-width: 340px !important;
  width: 540px !important;
  max-width: 700px !important;
}
.sbom-vuln-badges {
  display: flex;
  flex-direction: row;
  gap: 18px;
  align-items: center;
  min-height: 40px;
  padding-left: 2px;
}
.sbom-vuln-badge-wrap {
  display: flex;
  align-items: center;
  margin-right: 12px;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

/* Remove the blue background color on hover */
.sortable-header:hover {
  /* background-color: rgba(9, 66, 125, 0.9) !important; */
  /* Just add a subtle opacity change instead */
  opacity: 0.9;
}
</style>
